import React, { useState } from "react";
import { Loader2, Check, Phone, User, Mail, MapPin, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CITIES = [
  "Mumbai", "Delhi", "Bengaluru", "Hyderabad", "Chennai", "Kolkata", "Pune",
  "Ahmedabad", "Jaipur", "Lucknow", "Chandigarh", "Kochi", "Indore", "Bhopal",
  "Nagpur", "Surat", "Coimbatore", "Vadodara", "Visakhapatnam", "Other",
];

/**
 * LeadForm — dealer quote / test-drive capture widget.
 * Used inside CarDetail bottom-cta. Ships name/phone/city/email(opt)/variant(opt)
 * to POST /api/leads. Shows a success card on submit.
 */
export default function LeadForm({ car, variant = "price_quote", onSubmitted }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", city: CITIES[0], variantPref: "", consent: true,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please agree to be contacted by nearby dealers.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads", {
        car_slug: car.slug,
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        city: form.city,
        variant: form.variantPref || null,
        intent: variant,
        consent: !!form.consent,
      });
      setDone(true);
      toast.success("Your request is on its way to nearby dealers.");
      onSubmitted?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-3xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/20 p-8 text-center" data-testid="lead-form-success">
        <div className="w-14 h-14 rounded-full bg-emerald-500 text-white grid place-items-center mx-auto">
          <Check className="w-6 h-6"/>
        </div>
        <div className="font-display text-2xl mt-4 tracking-tight">You&apos;re all set, {form.name.split(" ")[0]}!</div>
        <p className="text-sm text-muted-foreground max-w-md mx-auto mt-2">
          We&apos;ve shared your interest in the <span className="font-medium">{car.brand_name} {car.model}</span> with the nearest {car.brand_name} dealers in {form.city}. Expect a callback within 24 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border border-border bg-card p-6 sm:p-8 shadow-sm" data-testid="lead-form">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-blue-700 dark:text-blue-300 font-semibold">
        <Sparkles className="w-3 h-3"/> Best on-road price
      </div>
      <h3 className="font-display text-2xl sm:text-3xl mt-2 tracking-tight">
        Get the sharpest quote for the {car.model}
      </h3>
      <p className="text-sm text-muted-foreground mt-2">
        Free · No spam · Nearest {car.brand_name} showrooms will reach out with their best price and current offers.
      </p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Field icon={User} label="Your name" testId="lead-name"
          value={form.name} onChange={(v) => set("name", v)} placeholder="Full name" required autoComplete="name"/>
        <Field icon={Phone} label="Mobile" testId="lead-phone" type="tel" inputMode="numeric"
          value={form.phone} onChange={(v) => set("phone", v.replace(/[^\d+\s-]/g, ""))} placeholder="10-digit number" required autoComplete="tel" maxLength={13}/>
        <Field icon={Mail} label="Email (optional)" testId="lead-email" type="email"
          value={form.email} onChange={(v) => set("email", v)} placeholder="you@email.com" autoComplete="email"/>
        <div>
          <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground inline-flex items-center gap-1.5 mb-1.5">
            <MapPin className="w-3 h-3"/> City
          </label>
          <select data-testid="lead-city" value={form.city} onChange={(e) => set("city", e.target.value)}
            className="w-full h-11 rounded-xl border border-border bg-white dark:bg-card px-3 text-sm outline-none focus:border-blue-400">
            {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5 block">
            Preferred variant (optional)
          </label>
          <input data-testid="lead-variant" value={form.variantPref} onChange={(e) => set("variantPref", e.target.value)}
            placeholder={car.variants?.[0]?.name ? `e.g. ${car.variants[0].name}` : "Any variant"}
            className="w-full h-11 rounded-xl border border-border bg-white dark:bg-card px-3 text-sm outline-none focus:border-blue-400"/>
        </div>
      </div>

      <label className="mt-4 flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
        <input type="checkbox" data-testid="lead-consent" checked={form.consent}
          onChange={(e) => set("consent", e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-border accent-blue-600"/>
        <span>
          I agree to be contacted by {car.brand_name} authorised dealers on the number above.
          TrendingNewCar does not share your data with third parties beyond this dealer request.
        </span>
      </label>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <button type="submit" disabled={busy} data-testid="lead-submit"
          className="h-11 px-6 rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 hover:opacity-95">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Sending…</> : <>Get best price</>}
        </button>
        <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
          <ShieldCheck className="w-3 h-3 text-emerald-600"/> Secure · India-based dealers only
        </span>
      </div>
    </form>
  );
}

function Field({ icon: Icon, label, value, onChange, placeholder, testId, type = "text", required, autoComplete, inputMode, maxLength }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground inline-flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3 h-3"/> {label}
      </label>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} required={required} autoComplete={autoComplete}
        inputMode={inputMode} maxLength={maxLength}
        className="w-full h-11 rounded-xl border border-border bg-white dark:bg-card px-3 text-sm outline-none focus:border-blue-400"/>
    </div>
  );
}
